import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { VenueTypeConfig } from '@lov/shared';

export const venueTypeKeys = {
  all: ['venue-types'] as const,
};

/**
 * Hook to fetch venue types from the config API
 * Uses regular query with 5-minute staleTime since config rarely changes
 */
export function useVenueTypes() {
  const result = useQuery({
    queryKey: venueTypeKeys.all,
    queryFn: async () => {
      // api.get already unwraps response.data, returns T directly
      return api.get<VenueTypeConfig[]>('/config/venue-types');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: result.data ?? [],
    isLoading: result.isLoading,
    error: result.error,
  };
}

/**
 * Helper to get venue type label by code
 */
export function getVenueTypeLabel(code: string, venueTypes: VenueTypeConfig[]): string {
  const venueType = venueTypes.find(vt => vt.code === code);
  return venueType?.name || code;
}

/**
 * Helper to get venue type color by code
 */
export function getVenueTypeColor(code: string, venueTypes: VenueTypeConfig[]): { bg: string; text: string } {
  const venueType = venueTypes.find(vt => vt.code === code);

  // Return custom colors if available, otherwise use defaults
  if (venueType?.color) {
    // Simple color generation based on the provided color
    return {
      bg: `${venueType.color}20`, // Add transparency
      text: venueType.color,
    };
  }

  // Fallback colors by code - using vibrant theme colors
  const defaults: Record<string, { bg: string; text: string }> = {
    GAMING: { bg: 'rgba(168, 85, 247, 0.12)', text: '#A855F7' },        // Vibrant purple
    SNOOKER: { bg: 'rgba(16, 185, 129, 0.12)', text: '#10B981' },       // Vibrant green
    KIDS_PLAYAREA: { bg: 'rgba(251, 146, 60, 0.12)', text: '#FB923C' }, // Vibrant orange
  };

  return defaults[code] || { bg: '#E5E7EB', text: '#374151' };
}
